import {
  PageSelector,
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@spz/ui/components/pagination";
import { cn } from "@spz/ui/lib/utils";
import { type JSX, useEffect, useRef } from "react";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";

interface TableFooterProps {
  // PageSelector props
  pageSize: number;
  totalCount: number;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];

  // Pagination props
  currentPage: number;
  totalPages: number;
  entityLabel: string;
  onPageChange: (page: number) => void;

  // Actions slot
  actions?: React.ReactNode;

  isLoading?: boolean;
  ref?: Ref<HTMLDivElement>;
  className?: string;
}

export const TableFooter = ({
  className,
  pageSize,
  totalCount,
  onPageSizeChange,
  pageSizeOptions,
  currentPage,
  totalPages,
  onPageChange,
  actions,
  isLoading,
  ref,
  entityLabel,
  ...props
}: TableFooterProps) => {
  const { t } = useTranslation();
  const displayPagination =
    pageSizeOptions &&
    pageSizeOptions.length > 0 &&
    totalCount > pageSizeOptions[0];

  // Keep track of the last stable pagination state
  const lastStableState = useRef({
    currentPage,
    totalPages,
  });
  // Update the stable state when not loading
  useEffect(() => {
    if (!isLoading) {
      lastStableState.current = {
        currentPage,
        totalPages,
      };
    }
  }, [isLoading, currentPage, totalPages]);

  // Pagination logic
  const generatePaginationItems = () => {
    // Use the last stable state during loading to prevent layout shifts
    const stableCurrentPage = isLoading
      ? lastStableState.current.currentPage
      : currentPage;
    const stableTotalPages = isLoading
      ? lastStableState.current.totalPages
      : totalPages;

    const items: JSX.Element[] = [];
    const maxVisiblePages = 5; // Maximum number of visible items including ellipsis
    const sideItems = 1; // Number of items to show on each side of current page

    // Helper function to add page button
    const addPageButton = (pageNum: number) => {
      items.push(
        <PaginationItem key={`page-${pageNum}`}>
          <PaginationButton
            isActive={stableCurrentPage === pageNum}
            onClick={() => onPageChange(pageNum)}
            data-testid={`pagination-page-${pageNum}`}
          >
            {pageNum}
          </PaginationButton>
        </PaginationItem>,
      );
    };

    // Helper function to add ellipsis
    const addEllipsis = (key: string) => {
      items.push(
        <PaginationItem key={`ellipsis-${key}`}>
          <PaginationEllipsis />
        </PaginationItem>,
      );
    };

    // Always show first page
    addPageButton(1);

    // Calculate visible range
    let startPage = Math.max(2, stableCurrentPage - sideItems);
    let endPage = Math.min(stableTotalPages - 1, stableCurrentPage + sideItems);

    // Adjust range for edge cases
    if (stableCurrentPage <= sideItems + 1) {
      endPage = Math.min(maxVisiblePages - 1, stableTotalPages - 1);
    }
    if (stableCurrentPage >= stableTotalPages - sideItems) {
      startPage = Math.max(2, stableTotalPages - maxVisiblePages + 2);
    }

    // Add first ellipsis if needed
    if (startPage > 2) {
      addEllipsis("start");
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      addPageButton(i);
    }

    // Add last ellipsis if needed
    if (endPage < stableTotalPages - 1) {
      addEllipsis("end");
    }

    // Always show last page if there's more than one page
    if (stableTotalPages > 1) {
      addPageButton(stableTotalPages);
    }

    return items;
  };

  return (
    <div
      ref={ref}
      className={cn(
        "h-8 flex border-t border-gray-200 bg-white rounded-b px-4",
        isLoading && "pointer-events-none",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex items-center justify-between w-full",
          actions && "pr-4",
        )}
      >
        <Pagination
          className={cn(
            "relative flex items-center justify-center",
            isLoading && "opacity-50",
          )}
        >
          {displayPagination && (
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={isLoading || currentPage === 1}
                  label={t("previous_page")}
                />
              </PaginationItem>

              <div className="hidden lg:flex items-center">
                {generatePaginationItems()}
              </div>
              <PaginationItem key={`page-${currentPage}`} className="lg:hidden">
                <PaginationButton
                  isActive={true}
                  className={cn(isLoading && "opacity-50")}
                  disabled={isLoading}
                >
                  {currentPage}
                </PaginationButton>
              </PaginationItem>

              <PaginationItem>
                <PaginationNext
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={
                    isLoading || currentPage === totalPages || !totalPages
                  }
                  label={t("next_page")}
                />
              </PaginationItem>
            </PaginationContent>
          )}
        </Pagination>

        <PageSelector
          pageSize={pageSize}
          currentPage={currentPage}
          totalCount={totalCount}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={pageSizeOptions}
          className={cn(isLoading && "opacity-50")}
          entityLabel={entityLabel}
        />
      </div>

      {actions}
    </div>
  );
};

TableFooter.displayName = "TableFooter";
